import Formatter from "app/Formatter";
import _ from "app/lang";
import { DeviceHealth } from "app/models/Device";
import { Model } from "app/models/Model";
import { staticContext } from "app/page/ContextProvider";
import { AlertType } from "shared/types";

// Currently, we have two types of the alerts for the devices.
// One is for the device in the sites, and the other one is for Smart NVR.
// Format defined:
// some attributes may not have when the firebase messaging gets the payload data.
// some attributes may be null when the data gets from the API.
type ConditionalProps =
    | {
        device_name: string;
        device_id: string;
        site_name: string;
        site_id: string;
        snvr_device_name?: null;
        snvr_device_id?: null;
    }
    | {
        device_name?: null;
        device_id?: null;
        site_name?: null;
        site_id?: null;
        snvr_device_name: string;
        snvr_device_id: string;
};

interface CommonProps {
    id: string;
    alert_type: AlertType;
    alert_data_1?: string;
    alert_data_2?: string;
    alert_data_3?: string;
    alert_data_json?: any;
    health_id: DeviceHealth;
    is_viewed?: boolean;
    problem_at?: string;
}

export type AlertAttributes = CommonProps & ConditionalProps;

type MessageFormatter = (vars: {}) => string;

const messages: Record<AlertType, string | MessageFormatter> = {
    DEV_CONFIG_FAILED: _("Device configuration failed."),
    DEV_UNREACHABLE: _("Unable to reach device."),
    DEV_REBOOT: _("Device rebooted."),
    BOOTBANK_REVERT: _("Bootbank reverted."),
    POWER_CONSUMPTION: _("Power consumption exceeded limit."),
    DEV_REQ_ACTION: _("Device has registration issues that require your attention."),
    MEDIA_SYNC_FAILURE: _("There was a problem when syncing the media to your device."),
    TIME_UNSYNCED: _("Time on device not in sync with the cloud."),
    LINK_60G_DOWN: _("MetroLinq 60 GHz link is down, 5 GHz failover activated (if enabled)."),
    STREAM_FAILED: _("Streaming failure."),
    TASK_FAILED: _("Maintenance task failure."),
    UCI_FILE_SYNC: _("File sync error."),
    CHANNEL_CHANGED: _("Channel changed on radio."),
    FW_UPGRADED: vars => _("Device firmware was upgraded from :data_1 to :data_2", vars),
    FW_DOWNGRADED: vars => _("Device firmware was downgraded from :data_1 to :data_2", vars),
    DEV_MANAGE_DEVICE_UNREACHABLE: vars => _("Unable to reach :data_1 :data_2 :data_3", vars),
    BATTERY_FULL: vars => _("IoT device battery is fully charged.\nCurrent battery level: :data_1%", vars),
    BATTERY_MEDIUM: vars => _("IoT device battery level has dropped below the configured threshold.\nCurrent battery level: :data_1%", vars),
    BATTERY_LOW: vars => _("IoT device battery level has dropped below the configured threshold.\nCurrent battery level: :data_1%", vars),
    BATTERY_CRITICAL: vars => _("IoT device battery level has dropped below the configured threshold.\nCurrent battery level: :data_1%", vars),
    DOOR_UNLOCKED: vars => _("Door unlocked. Method: :data_1, Time: :data_2", vars),
    DOOR_LOCKED: vars => _("Door locked. Method: :data_1, Time: :data_2", vars),
    DOOR_UNLOCK_FAILED: vars => _("Door unlock failed. Method: :data_1, Time: :data_2", vars),
    IOT_AP_ROAMING: _("IoT device roamed to a different AP."),
    SAUBLE_ALERT: vars => _(":data_3", vars),
    SWITCH_PORT_DOWN: vars => _("Switch port :data_1 link is down.", vars),
    SWITCH_PORT_UP: vars => _("Switch port :data_1 link is up.", vars),
    GAUGE_THRESHOLD: _("GaugeVision reading crossed its threshold.")
};

const DOOR_LOCK_TYPES: AlertType[] = ["DOOR_UNLOCKED", "DOOR_LOCKED", "DOOR_UNLOCK_FAILED"];

const METHOD_LABELS: { [key: string]: string } = {
    nfc: "NFC",
    password: "Password",
    cloud: "Cloud",
    app: "APP",
    auto: "Auto",
};

function formatDoorLockData(data1?: string, data2?: string) {
    const method = data1 ? (METHOD_LABELS[data1] || data1) : data1;
    let time = data2;

    if (data2 && /^\d+$/.test(data2)) {
        const formatter = new Formatter(staticContext.user);

        time = formatter.formatDateTime(new Date(parseInt(data2, 10) * 1000), { withSeconds: true });
    }

    return { data_1: method, data_2: time };
}

export class RecentAlert extends Model<AlertAttributes> {
    get message() {
        let message = messages[this.attrs.alert_type];

        if (typeof message === "function") {
            let vars: any = {
                data_1: this.attrs.alert_data_1,
                data_2: this.attrs.alert_data_2,
                data_3: this.attrs.alert_data_3,
            };

            if (DOOR_LOCK_TYPES.indexOf(this.attrs.alert_type) !== -1) {
                vars = { ...vars, ...formatDoorLockData(this.attrs.alert_data_1, this.attrs.alert_data_2) };
            }

            message = message(vars);
        }

        return message;
    }
}
